import { useContext } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { SystemInputs } from "@graylog/server-api";
import fetch from "logic/rest/FetchProvider";
import { qualifyUrl } from "util/URLUtils";
import UserNotification from "util/UserNotification";
import InputsContext from "contexts/InputsContext";
import i18n from "i18n";
const SOURCE_URL = "/system/inputs";
const INPUTS_QUERY_KEY = ["inputs", "list"];
export const fetchInputs = () => fetch("GET", qualifyUrl(SOURCE_URL));
export const fetchInput = (inputId) => fetch("GET", qualifyUrl(`${SOURCE_URL}/${inputId}`));
export const createInput = (input) => {
  const promise = fetch("POST", qualifyUrl(`${SOURCE_URL}?setup_wizard=true`), input);
  promise.then(
    () => {
      UserNotification.success(
        i18n.t("use_inputs_notifications.launched_success", { ns: "inputs", title: input.title })
      );
    },
    (error) => {
      UserNotification.error(
        i18n.t("use_inputs_notifications.launch_failed", { ns: "inputs", title: input.title, error }),
        i18n.t("use_inputs_notifications.launch_failed_title", { ns: "inputs" })
      );
    }
  );
  return promise;
};
export const updateInput = (id, input) => {
  const promise = fetch("PUT", qualifyUrl(`${SOURCE_URL}/${id}`), input);
  promise.then(
    () => {
      UserNotification.success(
        i18n.t("use_inputs_notifications.updated_success", { ns: "inputs", title: input.title })
      );
    },
    (error) => {
      UserNotification.error(
        i18n.t("use_inputs_notifications.update_failed", { ns: "inputs", title: input.title, error }),
        i18n.t("use_inputs_notifications.update_failed_title", { ns: "inputs" })
      );
    }
  );
  return promise;
};
export const deleteInput = (input) => {
  const promise = SystemInputs.terminate(input.id);
  promise.then(
    () => {
      UserNotification.success(
        i18n.t("use_inputs_notifications.deleted_success", { ns: "inputs", title: input.title })
      );
    },
    (error) => {
      UserNotification.error(
        i18n.t("use_inputs_notifications.delete_failed", { ns: "inputs", title: input.title, error }),
        i18n.t("use_inputs_notifications.delete_failed_title", { ns: "inputs" })
      );
    }
  );
  return promise;
};
export const inputsAsMap = (inputsList) => {
  const inputsMap = {};
  inputsList.forEach((input) => {
    inputsMap[input.id] = input;
  });
  return inputsMap;
};
const useInputsList = () => useQuery({
  queryKey: INPUTS_QUERY_KEY,
  queryFn: () => fetchInputs().catch((error) => {
    UserNotification.error(
      i18n.t("use_inputs_notifications.fetch_failed", { ns: "inputs", error }),
      i18n.t("use_inputs_notifications.fetch_failed_title", { ns: "inputs" })
    );
    throw error;
  }),
  select: (data) => data.inputs
});
export const useCreateInput = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input) => createInput(input),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: INPUTS_QUERY_KEY });
    }
  });
};
export const useInputs = () => useContext(InputsContext);
export { INPUTS_QUERY_KEY };
export default useInputsList;
